import notification from 'antd/lib/notification'
import getToken from './get-token'
import { currentRouteFeature } from './feature-from-route'

const REST_API_URL = process.env.REACT_APP_REST_API_HOST

export const getEndpointPath = (endpoint) => `${REST_API_URL}/${endpoint}`

// Exported for the unload-time paywall flush, which cannot go through
// makeRequest (it must build its request synchronously) but must still send
// the same workspace header, or a team user's events would resolve a
// different subscription_tier than the ones sent on the normal path.
export const getStoredWorkspaceUuid = () => {
  // eslint-disable-next-line no-undef
  if (typeof localStorage === 'undefined') return null
  // eslint-disable-next-line no-undef
  const overrideUuid = localStorage.getItem('activeWorkspaceOverride')
  // eslint-disable-next-line no-undef
  const overrideType = localStorage.getItem('activeWorkspaceOverrideType')
  if (overrideUuid) {
    return overrideType === 'team' ? overrideUuid : null
  }
  // eslint-disable-next-line no-undef
  const lastKnownUuid = localStorage.getItem('lastKnownActiveWorkspaceUuid')
  // eslint-disable-next-line no-undef
  const lastKnownType = localStorage.getItem('lastKnownActiveWorkspaceType')
  return lastKnownType === 'team' ? lastKnownUuid : null
}

const getResponseErrorPayload = responseData => {
  if (responseData?.error && typeof responseData.error === 'object') return responseData.error
  return responseData || {}
}

const makeRequest = async (method = 'POST', endpoint, data = {}, token, signal) => {
  const configs = {}
  if (signal) {
    Object.assign(configs, { signal })
  }
  token = token || (await getToken())
  const headers = {
    Authorization: `Bearer ${token}`
  }
  const workspaceUuid = getStoredWorkspaceUuid()
  if (workspaceUuid) {
    headers['x-workspace-id'] = workspaceUuid
  }
  // Declares the surface the user is on so the shared media endpoints can
  // attribute their spend to it. Sent on every request rather than only the
  // media ones: the API honours it only on the handful of routes that opt in,
  // and a per-endpoint allow-list here would be a second copy of that decision
  // to keep in sync.
  const routeFeature = currentRouteFeature()
  if (routeFeature) {
    headers['x-nolan-feature'] = routeFeature
  }
  if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
    // eslint-disable-next-line no-undef
    if (data instanceof FormData) {
      configs.body = data
    } else {
      configs.body = JSON.stringify(data)
      headers['Content-Type'] = 'application/json'
      headers.Accept = 'application/json'
    }
  }
  // eslint-disable-next-line no-undef
  const response = await fetch(`${REST_API_URL}/${endpoint}`, {
    method: method,
    ...configs,
    headers: headers
  })
  if (!response.ok) {
    // A non-JSON error body (an ALB/CloudFront 502 or 504 HTML page, say) used
    // to throw a SyntaxError right here, destroying `error.status` and with it
    // any chance of status-specific handling upstream.
    let responseData = {}
    try {
      responseData = await response.json()
    } catch (parseError) {
      responseData = {}
    }
    const responseError = getResponseErrorPayload(responseData)
    const error = new Error(response.statusText)
    error.status = response.status
    error.actions = responseError.actions || responseData.actions
    error.code = responseError.code || responseData.code
    error.declineCode = responseError.declineCode || responseData.declineCode
    error.message = responseError.message || responseData.message || error.message
    if (error.code === 'LIMITED_ACCESS') {
      notification.warning({
        message: responseError.title || responseData.title,
        description: error.message
      })
      delete error.message
    }
    throw error
  }
  return response
}

export const toJSON = async (method = 'POST', endpoint, data = {}, token, signal) => {
  const response = await makeRequest(method, endpoint, data, token, signal)
  return response.json()
}

export const toBlob = async (method = 'POST', endpoint, data = {}, token, signal) => {
  const response = await makeRequest(method, endpoint, data, token, signal)
  // eslint-disable-next-line no-undef
  return response.blob()
}

export const toAudio = async (method = 'POST', endpoint, data = {}, token) => {
  const response = await makeRequest(method, endpoint, data, token)
  const res = await response.json()
  // Convert base64 to binary and create Blob URL
  // eslint-disable-next-line no-undef
  return convertToAudio(res.audio)
}

export const convertToAudio = (audio) => {
  // Convert base64 to binary and create Blob URL
  // eslint-disable-next-line no-undef
  const binaryString = atob(audio)
  const binaryLength = binaryString.length
  const bytes = new Uint8Array(binaryLength)
  for (let i = 0; i < binaryLength; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // eslint-disable-next-line no-undef
  const audioBlob = new Blob([bytes], { type: 'audio/mpeg' })
  const audioUrl = URL.createObjectURL(audioBlob)
  return audioUrl
}

export const headless = async (method = 'POST', endpoint, data = {}, token, signal) => {
  return makeRequest(method, endpoint, data, token, signal)
}

export const withStream = async (response, responseType = 'json', cb) => {
  if (response.body && typeof response.body.getReader === 'function') {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let done = false
    const parseMessage = (text) => {
      const payload = text
        .trim()
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith(':') && !line.startsWith('event:') && !line.startsWith('id:'))
        .map((line) => line.startsWith('data:') ? line.replace(/^data:\s*/, '') : line)
        .join('\n')
        .trim()

      if (!payload || payload === '[DONE]') {
        return
      }

      const message = JSON.parse(payload)
      if (cb) {
        cb(null, message)
      }
    }

    while (!done) {
      const { value, done: readerDone } = await reader.read()
      done = readerDone
      if (value) {
        buffer += decoder.decode(value, { stream: !readerDone })
        const messages = buffer.split(/\n\n+/)
        buffer = messages.pop() || ''
        for (const message of messages) {
          parseMessage(message)
        }
      }
    }
    buffer += decoder.decode()
    if (buffer.trim()) {
      parseMessage(buffer)
    }
    return null
  }
  return response[responseType]()
}

export const createQueryString = (data) => {
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  )
  return new URLSearchParams(filteredData)
}
